import { AnyAction } from 'redux';
import { DocumentStatus } from '@synergy/models';
import { PaginatedResponse } from '@synergy/api';

import {
    CANCELED_INVOICE,
    CANCEL_INVOICE,
    COMPLETE_INVOICES_SUCCESS,
    DELETE_INVOICES,
    DUPLICATED_INVOICE,
    DUPLICATE_INVOICE,
    EXPORT_INVOICES,
    RECEIVE_INVOICES,
    RECEIVE_LATEST_EXPORT,
    REQUEST_INVOICE_END,
    REQUEST_INVOICE_START,
    RESET_INVOICES_STATE,
    RESET_LATEST_EXPORT,
} from './invoice-actions';
import { CompleteInvoiceResponse } from '../../invoice-api';
import { ExportInvoice } from '../models/export-invoice';
import { Invoice } from '../../models/invoice';

export interface InvoiceReducerState {
    flat: PaginatedResponse<Invoice>;
    requesting: boolean;
    latestExport?: ExportInvoice;
    finishInvoiceLoading: boolean;
}

const initialInvoiceReducerState: InvoiceReducerState = {
    flat: { limit: 10, page: 0, total: 0, items: [] },
    requesting: false,
    finishInvoiceLoading: false,
};

export const invoicesReducer = (
    state: InvoiceReducerState = initialInvoiceReducerState,
    action: AnyAction
): InvoiceReducerState => {
    const { type, ...rest } = action;
    switch (type) {
        case RECEIVE_INVOICES: {
            return {
                ...state,
                flat: action['invoices'],
            };
        }

        case DUPLICATED_INVOICE: {
            const nextItems = [action['invoice'], ...state.flat.items].slice(0, state.flat.limit);

            return {
                ...state,
                flat: { ...state.flat, items: nextItems },
                requesting: false,
            };
        }

        case RESET_INVOICES_STATE: {
            return {
                ...state,
                flat: initialInvoiceReducerState.flat,
            };
        }

        case DUPLICATE_INVOICE:
        case CANCEL_INVOICE:
        case REQUEST_INVOICE_START: {
            return {
                ...state,
                requesting: true,
            };
        }

        case REQUEST_INVOICE_END: {
            return {
                ...state,
                requesting: false,
            };
        }

        case DELETE_INVOICES: {
            return {
                ...state,
                flat: {
                    ...state.flat,
                    items: state.flat.items.filter(item => !rest['ids'].includes(item.id)),
                },
            };
        }

        case CANCELED_INVOICE: {
            const id = rest['invoiceId'] as number;

            const index = state.flat.items.findIndex(item => item.id === id);
            const itemsClone = [...state.flat.items];
            itemsClone[index] = { ...itemsClone[index], status: DocumentStatus.Canceled };

            return {
                ...state,
                flat: {
                    ...state.flat,
                    items: itemsClone,
                },
            };
        }

        case COMPLETE_INVOICES_SUCCESS: {
            const results = rest['results'] as CompleteInvoiceResponse[];
            const itemsClone = [...state.flat.items];

            results.forEach(result => {
                const index = itemsClone.findIndex(item => item.id === result.id);
                if (index !== -1) {
                    itemsClone[index] = {
                        ...itemsClone[index],
                        status: result.status as DocumentStatus,
                        number: result.number,
                    };
                }
            });

            return {
                ...state,
                flat: {
                    ...state.flat,
                    items: itemsClone,
                },
            };
        }

        case RECEIVE_LATEST_EXPORT: {
            return {
                ...state,
                latestExport: rest['latestExport'] ?? { highestReceiptNumber: 0 },
            };
        }

        case EXPORT_INVOICES: {
            return {
                ...state,
                latestExport: undefined,
            };
        }

        case RESET_LATEST_EXPORT: {
            return {
                ...state,
                latestExport: undefined,
            };
        }

        default: {
            return state;
        }
    }
};
